import React from 'react';
import { TimesButton } from '@fiverr-private/button';
import { getContext } from '@fiverr-private/fiverr_context';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Overlay } from '@fiverr-private/overlay';
import { Tooltip } from '@fiverr-private/tooltip';
import { Typography } from '@fiverr-private/typography';
import Asset from '../../Asset';
import { useActiveState } from '../../hooks/useActiveState';
import RemoveButton from '../RemoveButton';
import type { AttachmentProps } from '../types';
import { renderAttachmentsChildren } from '../../utils';

const AttachmentGridItem = ({
  showPreview,
  onRemove,
  error,
  onClick,
  width,
  height,
  hasError,
  url,
  name,
  children,
  variant,
  theme,
}: AttachmentProps) => {
  const { isActive, handleMouseEnter, handleMouseLeave, handleFocus, handleBlur } = useActiveState();
  const { isTouch } = getContext();

  const tooltipContent = (
    <Stack direction="column" gap="0" color="inherit">
      <Typography>{name}</Typography>
      {error && <Typography>{error}</Typography>}
    </Stack>
  );

  return (
    <Container
      paddingTop="2"
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
    >
      <Tooltip content={tooltipContent} appendToBody as="div" withDelay={false} size="sm">
        <Container onClick={onClick} position="relative" tabIndex={0} width="fit-content" borderRadius="lg">
          <Container justifyContent="flexEnd">
            {isTouch && (
              <Container position="absolute" top="-8px" right="-6px" zIndex="1">
                <TimesButton
                  onClick={onRemove}
                  ariaLabel="delete"
                  size="3xs"
                  theme="dark"
                  variant="filled"
                  shape="circle"
                />
              </Container>
            )}

            <Asset
              url={url}
              name={name}
              hasError={hasError}
              variant={variant}
              showPreview={showPreview}
              width={width}
              height={height}
              theme={theme}
            >
              {isTouch ? renderAttachmentsChildren(children, { variant, theme }) : null}
            </Asset>
          </Container>
          {isActive && !isTouch && (
            <Overlay lockBodyScroll={false} position="absolute" borderRadius="lg">
              <Stack direction="column" gap="1" alignItems="flexEnd" padding="2" height="100%">
                <RemoveButton onClick={onRemove} variant={variant} theme={theme} />
                {hasError && renderAttachmentsChildren(children, { variant, theme })}
              </Stack>
            </Overlay>
          )}
        </Container>
      </Tooltip>
    </Container>
  );
};

AttachmentGridItem.displayName = 'AttachmentGridItem';
export default AttachmentGridItem;
